import { Report } from 'notiflix'
import { apiDownload } from '../../../helpers/apiRequester'
import { PillTone, StatusPill } from '../../../ui'
import { LINK_STATE_MEANING } from '../commsShared/linkStates'

export type LinkState = 'linked' | 'candidates' | 'unlinked' | 'not_applicable'
export type TriageState = 'pending' | 'in_progress' | 'resolved' | 'snoozed' | 'archived'

export interface ReplyListItem {
    id: string
    matchedSendId: string | null
    matchMethod: string
    matchConfidence: number | null
    fromEmail: string
    toEmail: string
    subject: string | null
    receivedAt: string | null
    manualReviewState: string
    triageState: TriageState
    classification: string | null
    classificationConfidence: number | null
    classificationSource: string | null
    classifiedAt: string | null
    claimRef: string | null
    clientRef: string | null
    lenderName: string | null
    /** Comms management: the case (client/claim) this email is linked to, letter or not. */
    caseId?: string | null
    caseStatus?: string | null
    introducer?: string | null
    clientName?: string | null
    /** Letter linking: type of the letter this email is about (when one is chosen). */
    sendKind?: SendKind | string | null
    batchName: string | null
    attachmentCount: number
    linkState: LinkState
    unlinkedReason: string | null
    partyType: string | null
    aiSummary: string | null
    suggestedNextAction: string | null
    extractionState: string | null
    sourceProvider: string
    sourceMailbox: string | null
    sourceFolder: string | null
    candidateCount: number
    /** Thread this email belongs to. */
    conversationId?: string | null
    disputedFactCount: number
    disagreeingFactCount: number
}

export type DocumentKind = 'cover' | 'foa' | 'loc'
export const DOCUMENT_LABEL: Record<DocumentKind, string> = { loc: 'Letter of complaint', cover: 'Cover letter', foa: 'Form of authority' }

/** Types of letter we send (comms_outbound.send_kind), in the order they are shown. */
export type SendKind = 'initial_loc' | 'chaser' | 'ref_request' | 'info_response' | 'new_loa' | 'settlement_response' | 'custom'
export const SEND_KIND_LABEL: Record<string, string> = {
    initial_loc: 'Letter of complaint',
    chaser: 'Chaser',
    ref_request: 'Reference request',
    info_response: 'Information response',
    new_loa: 'New letter of authority',
    settlement_response: 'Settlement response',
    custom: 'Custom letter',
    none: 'Not about a specific letter',
}
export const sendKindLabel = (kind: string | null | undefined) => (kind ? SEND_KIND_LABEL[kind] ?? humanise(kind) : SEND_KIND_LABEL.none)

/** One outbound LOC email as returned by comms/inbox/case and comms/inbox/send. */
export interface CaseSend {
    id: string
    caseId: string | null
    claimRef: string
    clientRef: string | null
    lenderName: string | null
    sendKind?: SendKind | string | null
    sendKindLabel?: string | null
    batchId: string | null
    toEmail: string
    fromEmail: string | null
    subject: string | null
    footerRef: string | null
    status: string
    queuedAt: string | null
    sentAt: string | null
    deliveredAt: string | null
    openCount: number
    firstOpenedAt: string | null
    lastError: string | null
    documents: DocumentKind[]
}

export interface SentEmailEvent {
    id: string
    type: string
    at: string
    bounceType: string | null
    bounceSubtype: string | null
    diagnosticCode: string | null
    userAgent: string | null
    linkUrl: string | null
}

export interface SentEmailDetail extends CaseSend {
    replyToEmail: string | null
    bodyText: string | null
    bodyHtml: string | null
    rfcMessageId: string | null
    sesMessageId: string | null
    introducer: string | null
    events: SentEmailEvent[]
}

export interface CaseView {
    id: string
    claimRef: string
    lenderNormalisedName: string | null
    status: string
    openedAt: string | null
    firstSendId: string | null
    ackDeadline: string | null
    substantiveDeadline: string | null
    lastActivityAt: string | null
    closedAt: string | null
    closedReason: string | null
    backfilled: boolean
    lenderRefs: string[]
}

export interface IdentityView {
    introducer: string | null
    lenderName: string | null
    clientName: string | null
    clientPostcode: string | null
    clientEmail: string | null
    agreementNumber: string | null
    vehicleRegistration: string | null
    vehicle: string | null
    proclaimReference: string | null
    agreementStartDate: string | null
    snapshotAt: string | null
    snapshotError: string | null
}

export type Validation = 'verified' | 'unanimous' | 'majority' | 'disputed' | 'single' | 'human'

export interface Candidate {
    id: number
    claimRef: string
    outboundId: string | null
    caseId: string | null
    method: string
    score: number
    reasons: Record<string, unknown> | string[] | null
    state: 'suggested' | 'auto_linked' | 'confirmed' | 'rejected'
    decidedBy: string | null
}

export interface FactCandidate {
    runId: string
    modelKey: string
    role: string
    value: unknown
}

export interface Fact {
    id: number
    factType: string
    label: string
    group: string
    dataType: string
    isIdentifier: boolean
    valueRaw: string
    valueNorm: string | null
    unit: string | null
    confidence: number | null
    sourceSpan: string | null
    validation: Validation
    agreeingRunIds: string[] | null
    candidates: FactCandidate[] | null
    inferenceRunId: string | null
    homeValue: string | null
    comparison: 'agree' | 'disagree' | 'new' | 'unknown'
    decidedBy: string | null
}

export interface InferenceRun {
    id: string
    task: string
    modelKey: string
    provider: string
    modelId: string
    role: string
    status: string
    inputTokens: number | null
    outputTokens: number | null
    costPence: number | null
    latencyMs: number | null
    error: string | null
    createdAt: string
}

export interface Extraction {
    id: string
    task: string
    promptVersion: string
    schemaVersion: string
    voting: string | null
    consensusState: string | null
    disputedFieldCount: number
    summary: string | null
    classification: string | null
    classificationConfidence: number | null
    classificationValidation: string | null
    proposedCategory: string | null
    proposedRationale: string | null
    suggestedNextAction: string | null
    costPence: number | null
    extractedAt: string
}

export interface RuleHit {
    id: string
    ruleName: string
    classification: string
    mode: string
    wasShadow: boolean
    snippet: string | null
}

export interface ReplyDetail extends ReplyListItem {
    inReplyTo: string | null
    bodyText: string | null
    bodyHtml: string | null
    strippedBodyText: string | null
    rawS3Key: string | null
    classifiedBy: string | null
    classificationPayload: { validation?: string; candidates?: FactCandidate[]; proposedCategory?: string | null } | null
    triageNotes: string | null
    extractionError: string | null
    matchedBy: string | null
    rfcMessageId: string | null
    locations: { mailbox: string; folder: string | null; seenAt: string }[]
    candidates: Candidate[]
    facts: Fact[]
    extraction: Extraction | null
    inferenceRuns: InferenceRun[]
    ruleHits: RuleHit[]
    /** The client/claim this email is about (comms management). */
    case: { id: string; claimRef: string | null; lenderName: string | null; status: string | null; introducer: string | null; clientName: string | null; clientEmail: string | null; clientPostcode: string | null } | null
    /** Which of our letters it is about, and how that was decided (letter linking). */
    letter: { sendId: string; sendKind: string | null; sendKindLabel: string | null; how: 'exact' | 'inferred'; by: string | null } | null
    send: { id?: string; claimRef: string | null; lenderName: string | null; sendKind?: string | null; batchName: string | null; footerRef: string | null; toEmail: string | null; sentAt: string | null } | null
    attachments: { id: string; filename: string; pageCount: number | null; hasExtractedText: boolean; rejectedReason: string | null }[]
}

/** A claim found by comms/inbox/claim-search. */
export interface ClaimSearchResult {
    claimRef: string
    introducer: string | null
    lenderName: string | null
    clientName: string | null
    clientEmail: string | null
    clientPostcode: string | null
    clientDob: string | null
    agreementNumber: string | null
    vehicleRegistration: string | null
    vehicle: string | null
    caseCount: number
    letterCount: number
    lastLetterAt: string | null
}

/** One letter with its attached emails, from comms/inbox/letters. */
export interface LetterWithReplies extends CaseSend {
    sendKind: SendKind | string
    sendKindLabel: string
    replies: { id: string; subject: string | null; fromEmail: string; receivedAt: string | null; classification: string | null; linkState: LinkState; triageState: TriageState }[]
}

export const pence = (v: number | null | undefined) => (v == null ? '—' : `£${(v / 100).toFixed(v < 100 ? 3 : 2)}`)

/** Display a fact value: GBP normalised values are stored in pence. */
export const formatFactValue = (f: Pick<Fact, 'valueRaw' | 'valueNorm' | 'unit'>) => {
    if (f.unit === 'GBP' && f.valueNorm != null && f.valueNorm !== '' && !Number.isNaN(Number(f.valueNorm))) return `£${(Number(f.valueNorm) / 100).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    return f.valueRaw
}

export interface CaseBundle {
    claimRef: string
    cases: CaseView[]
    identity: IdentityView | null
    sends: CaseSend[]
    replies: ReplyListItem[]
}

export const formatDateTime = (raw: string | null | undefined) => {
    if (!raw) return '—'
    try {
        return new Date(raw).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    } catch {
        return raw
    }
}

export const formatDate = (raw: string | null | undefined) => {
    if (!raw) return '—'
    try {
        return new Date(raw).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
    } catch {
        return raw
    }
}

export const humanise = (v: string | null | undefined) => (v ? v.replace(/_/g, ' ') : '—')

export const LinkBadge = ({ state, method, confidence }: { state: LinkState; method: string; confidence: number | null }) => {
    const tone: PillTone = state === 'linked' ? 'good' : state === 'candidates' ? 'warn' : state === 'not_applicable' ? 'neutral' : 'bad'
    const text = state === 'linked' ? 'Linked to claim' : state === 'candidates' ? 'Possible matches' : state === 'not_applicable' ? 'No claim expected' : 'Not linked'
    const title = state === 'linked' ? `${describeMatchMethod(method) ?? humanise(method)}${confidence != null ? ` (${confidence}% confidence)` : ''}` : LINK_STATE_MEANING[state]
    return (
        <StatusPill tone={tone} size="sm" dot title={title}>
            {text}
        </StatusPill>
    )
}

/** Small pill naming the type of letter an email is about; nothing when no letter is chosen. */
export const LetterTypePill = ({ sendKind, how }: { sendKind: string | null | undefined; how?: 'exact' | 'inferred' | null }) => {
    if (!sendKind) return null
    const title = how === 'inferred' ? 'Letter inferred from the claim (the only obvious letter); a person can change it' : how === 'exact' ? 'The email answers this letter directly' : undefined
    return (
        <StatusPill tone="info" size="sm" title={title}>
            {sendKindLabel(sendKind)}
        </StatusPill>
    )
}

export const sendStatusTone = (status: string): PillTone =>
    status === 'delivered' || status === 'sent' ? 'good' : status === 'bounced' || status === 'failed' || status === 'complained' ? 'bad' : status === 'suppressed' || status === 'cancelled' ? 'neutral' : 'warn'

export const caseStatusTone = (status: string): PillTone =>
    status.startsWith('closed') || status === 'settled' ? 'good' : status.includes('escalated') || status.includes('overdue') ? 'bad' : status.startsWith('awaiting') || status.startsWith('waiting') || status.includes('pending') ? 'warn' : 'neutral'

export const deadlineTone = (iso: string | null, closed: boolean): PillTone => {
    if (!iso || closed) return 'neutral'
    const days = (new Date(iso).getTime() - Date.now()) / 86_400_000
    return days < 0 ? 'bad' : days < 7 ? 'warn' : 'neutral'
}

/** "3 days left" / "5 days overdue" / "today" for a deadline. */
export const describeDeadline = (iso: string | null) => {
    if (!iso) return null
    const days = Math.round((new Date(iso).getTime() - Date.now()) / 86_400_000)
    if (days === 0) return 'due today'
    return days > 0 ? `${days} day${days === 1 ? '' : 's'} left` : `${-days} day${days === -1 ? '' : 's'} overdue`
}

/** Plain-English wording for how an email was tied to a case. */
export const describeMatchMethod = (method: string | null | undefined) => {
    switch (method) {
        case 'thread_header':
            return 'The lender replied directly to our email'
        case 'plus_address':
            return 'Sent to the case-specific reply address'
        case 'claim_ref':
            return 'Our claim reference appears in the email, so we know the client and claim'
        case 'footer_ref':
            return 'Our letter footer reference appears in the email'
        case 'tracking_token':
            return 'Our tracking reference appears in the email'
        case 'lender_ref':
            return "The lender's own reference matches an earlier email on this case"
        case 'identity':
            return 'Client details in the email (name, agreement, registration…) matched the case'
        case 'manual':
            return 'Linked by a team member'
        default:
            return null
    }
}

/** Open a rendered PDF for a sent LOC in a new tab (same flow as the LOC batches page). */
export const openSendDocument = async (sendId: string, kind: DocumentKind) => {
    try {
        const resp = await apiDownload('comms/loc-batches/attachment', { sendId, kind })
        const url = URL.createObjectURL(resp.data as Blob)
        if (!window.open(url, '_blank')) Report.info('Pop-up blocked', 'Allow pop-ups for this site to open documents in a new tab.', 'Okay')
        setTimeout(() => URL.revokeObjectURL(url), 60000)
    } catch (err: any) {
        let message = 'Could not open the document.'
        const data = err?.response?.data
        if (data instanceof Blob) {
            try {
                message = JSON.parse(await data.text())?.message || message
            } catch {
                /* keep default */
            }
        } else if (data?.message) message = data.message
        Report.failure('Document', message, 'Okay')
    }
}
